import React, { Fragment, useEffect, useState } from "react";
import Button from "../../components/Button";
import DesktopContainerAuth from "../../components/DesktopContainerAuth";
import Label from "../../components/Label";
import Loading from "../../components/Loading";
import ValidationPassword from "../../components/ValidationPassword";
import TermOfUse from "../../components/Views/TermOfUse";
import handleErrors from "../../helpers/handleErrors";
import { schemaNewPassword } from "./validation";
import { useHistory } from "react-router-dom";
import { changePassword } from "services/auth";
import PasswordInput from "components/PasswordInput";
import ContactAuth from "components/Views/ContactAuth";
import toastMessage from "helpers/handleToastMessage";
import { ToastContainer } from "react-toastify";
import jwt_decode from "jwt-decode";
import { useAuth } from "context/auth";

type Props = {};

const NewPassword: React.FC<Props> = (): JSX.Element => {
  const { setUserName, setUserRole } = useAuth();

  let history = useHistory();

  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [isHidden, setIsHidden] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<any>();

  const [state, setState] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  const [isValidMin8Characters, setIsValidMin8Characters] =
    useState<boolean>(false);
  const [isValidCharacters, setIsValidCharacters] = useState<boolean>(false);
  const [isValidSamePassword, setIsValidSamePassword] =
    useState<boolean>(false);

  useEffect(() => {
    localStorage.clear();
    setRole(history.location.pathname.split("/")[2]);
    localStorage.setItem("role", role);

    if (history.location.state.state && history.location.state.email) {
      setState(history.location.state.state);
      setEmail(history.location.state.email);
    }
  }, [history, role]);

  useEffect(() => {
    setIsHidden(hidden(password, newPassword));
    const validMin8Characters = (password: string): boolean => {
      if (password.trim() === "") return false;
      return password.length >= 8;
    };

    const validNumericLetters = (password: string): boolean => {
      if (password.trim() === "") return false;
      if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])/.test(password)) return false;
      return true;
    };

    const validSamePassword = (password: string, confirm: string): boolean => {
      if (password.trim() === "" && confirm.trim() === "") return true;
      if (password !== confirm) return false;
      return true;
    };

    const isValidMin8Characters = validMin8Characters(password);
    const isValidCharacters = validNumericLetters(password);
    const isValidSamePassword = validSamePassword(password, newPassword);

    setIsValidMin8Characters(isValidMin8Characters);
    setIsValidCharacters(isValidCharacters);
    setIsValidSamePassword(isValidSamePassword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [password, newPassword]);

  const hidden = (password: string, confirm: string): boolean => {
    if (password.trim() === "" && confirm.trim() === "") return true;
    return false;
  };

  const addClassButton = () => {
    if (errors?.password || errors?.newPassword) {
      return "bg-bt-red text-white";
    }

    if (isValidMin8Characters && isValidCharacters && isValidSamePassword) {
      return "bg-bt-green text-white";
    }

    return "bg-bt-light-gray text-black";
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      if (!schemaNewPassword.isValidSync({ password, newPassword })) {
        handleErrors({
          schema: schemaNewPassword,
          stateData: { password, newPassword },
          setErrorMessage: setErrors,
        });
        setLoading(false);
        return;
      }

      if (
        !isValidMin8Characters ||
        !isValidCharacters ||
        !isValidSamePassword
      ) {
        setErrors({
          password: "Invalid password",
          newPassword: "Invalid password",
        });
        return;
      }

      const { data } = await changePassword({ email, password, state, role });
      if (data) {
        localStorage.setItem("token", JSON.stringify(data.accessToken));
        let decoded: any = jwt_decode(data.accessToken);
        setUserName(JSON.stringify(decoded.user?.name));
        setUserRole(JSON.stringify(decoded.user?.role));

        toastMessage({
          message: "Change password successfully.",
          type: "success",
        });

        if (role === "admin") {
          history.push("/entity-management");
        } else {
          history.push("/manage-families");
        }

        window.location.reload();
      }
    } catch (error: any) {
      toastMessage({
        message: "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };
  return (
    <DesktopContainerAuth>
      <Loading loading={loading} />
      <div className="flex flex-col">
        <Fragment>
          <div className="w-96 mx-auto">
            <Label
              label={"Now create your new password"}
              className="font-light text-xl text-bt-purple"
            />
            <Label
              label={
                "Should be at least 8 characters, and contain numbers and letters"
              }
              style={{
                color: "#4D4F5C",
                opacity: 0.5,
              }}
              className="mt-4 text-center font-light text-base"
            />
            <PasswordInput
              placeholder={"Password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isHelp={errors?.password}
              helpText={errors?.password}
            />

            <ValidationPassword
              isHidden={isHidden}
              password={password}
              passwordConfirm={newPassword}
            />

            <PasswordInput
              placeholder={"Repeat new password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              isHelp={errors?.newPassword}
              helpText={errors?.newPassword}
            />
            <Button
              label={"Set new password"}
              className={`mt-12 rounded-lg font-sora font-normal2 text-base h-8 w-full ${addClassButton()}`}
              onClick={handleSubmit}
            />
          </div>
          <ContactAuth
            title={"Request your access"}
            subTitle={
              "Are you an adoption professional and feel this would be the perfect solution for your business?"
            }
            titleButton={"Contact form"}
            action={"/signup/entity"}
          />
          <TermOfUse />
        </Fragment>
      </div>
      <ToastContainer />
    </DesktopContainerAuth>
  );
};

export default NewPassword;
